import React, { useRef } from "react";
import { Button, Skeleton, Tag, Tooltip } from "antd";
import { SettingOutlined } from "@ant-design/icons";

import { hexToRgba } from "utils/color";
import { useFormSummary, useInViewport } from "./useFormSummary";

const formatNumber = (value) => new Intl.NumberFormat("pt-BR").format(value);

/**
 * Linha da listagem: identificação, categorias e quantidade de registros do
 * formulário. O total só é buscado quando a linha entra na viewport
 * (ver useFormSummary).
 */
function FormRow({ form, accent, canConfig, canRecords, onOpen }) {
  const rowRef = useRef(null);
  const isVisible = useInViewport(rowRef);
  const { total, error } = useFormSummary(form.name, isVisible);

  const renderTotal = () => {
    if (error) return <span className="forms-row__muted">—</span>;
    // Sem dado ainda: ou a linha não chegou na viewport, ou a busca está em
    // andamento. Em ambos os casos o esqueleto é a leitura correta — mostrar
    // "0" faria o formulário parecer vazio
    if (total === null) {
      return <Skeleton.Button active size="small" style={{ width: 56, height: 18, minWidth: 56 }} />;
    }
    return <span>{formatNumber(total)}</span>;
  };

  return (
    // A fita da categoria é a borda esquerda do card
    <div ref={rowRef} className="forms-row" style={{ borderLeftColor: accent }}>
      <div
        className="forms-list__row forms-row__main"
        onClick={() => canRecords && onOpen("registros")}
        style={{ cursor: canRecords ? "pointer" : "default" }}
        role={canRecords ? "button" : undefined}
        tabIndex={canRecords ? 0 : undefined}
        onKeyDown={(event) => {
          if (canRecords && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            onOpen("registros");
          }
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div className="forms-row__name" title={form.shortName}>
            {form.shortName}
          </div>
          {form.description && (
            <div className="forms-row__description" title={form.description}>
              {form.description}
            </div>
          )}
        </div>

        <div className="forms-list__col--category forms-row__tags">
          {form.categories.map((category) => (
            <Tag
              key={category.name}
              className="forms-row__tag"
              style={{
                backgroundColor: hexToRgba(category.color, 0.12),
                borderColor: hexToRgba(category.color, 0.35),
                color: category.color,
              }}
            >
              {category.name}
            </Tag>
          ))}
        </div>

        <div className="forms-list__col--records forms-row__metric">{renderTotal()}</div>

        <div
          className="forms-list__col--actions forms-row__actions"
          onClick={(event) => event.stopPropagation()}
        >
          {canRecords && (
            <Button type="primary" onClick={() => onOpen("registros")}>
              Registros
            </Button>
          )}
          {canConfig && (
            <Tooltip title="Configurações">
              <Button
                icon={<SettingOutlined />}
                onClick={() => onOpen("detalhes")}
                aria-label={`Configurações de ${form.shortName}`}
              />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}

export default FormRow;
